import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useI18n } from '../../contexts/I18nContext';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { 
  Users, 
  Wallet, 
  Building2, 
  TrendingUp,
  Loader2,
  CheckCircle2,
  Clock,
  AlertCircle,
  ArrowUpRight,
  ArrowDownRight
} from 'lucide-react';

const AdminReportsPage = () => {
  const { formatCurrency } = useI18n();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await api.get('/admin/dashboard/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" strokeWidth={1.5} />
      </div>
    );
  }

  const capitalByStatus = stats?.commitment_stats || {};
  const usersByStatus = stats?.users_by_status || {};
  const propertiesByStatus = stats?.properties_by_status || {};

  // Calculate totals
  const totalCapitalCommitted = Object.values(capitalByStatus).reduce((sum, s) => sum + (s.amount || 0), 0);
  const confirmedCapital = (capitalByStatus.confirmed?.amount || 0) + (capitalByStatus.approved?.amount || 0);
  const pendingCapital = (capitalByStatus.pending?.amount || 0) + (capitalByStatus.funds_pending?.amount || 0);

  const totalUsers = Object.values(usersByStatus).reduce((sum, count) => sum + count, 0);
  const approvedUsers = usersByStatus.approved || 0;
  const pendingUsers = (usersByStatus.pending || 0) + (usersByStatus.in_review || 0);

  const totalProperties = Object.values(propertiesByStatus).reduce((sum, count) => sum + count, 0);
  const approvedProperties = propertiesByStatus.approved || 0;
  const inReviewProperties = (propertiesByStatus.submitted || 0) + (propertiesByStatus.initial_review || 0) + 
                            (propertiesByStatus.legal_review || 0) + (propertiesByStatus.technical_review || 0);

  // Funnel calculations
  const conversionRate = totalUsers > 0 ? ((approvedUsers / totalUsers) * 100).toFixed(1) : 0;
  const investorRate = approvedUsers > 0 ? (((capitalByStatus.confirmed?.count || 0) / approvedUsers) * 100).toFixed(1) : 0;

  return (
    <div className="space-y-8" data-testid="admin-reports-page">
      {/* Header */}
      <div>
        <h1 className="heading-2 mb-2">Panel Ejecutivo</h1>
        <p className="body-text">Métricas clave del desempeño de la plataforma.</p>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="card-dashboard">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Capital Total Comprometido</p>
                <p className="text-3xl font-light text-primary">{formatCurrency(totalCapitalCommitted)}</p>
                <p className="text-xs text-muted-foreground mt-2">
                  {formatCurrency(confirmedCapital)} confirmado
                </p>
              </div>
              <div className="p-3 bg-primary/10 rounded-full">
                <Wallet className="h-6 w-6 text-primary" strokeWidth={1.5} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-dashboard">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Usuarios Registrados</p>
                <p className="text-3xl font-light">{totalUsers}</p>
                <p className="text-xs text-muted-foreground mt-2">
                  {approvedUsers} aprobados ({conversionRate}%)
                </p>
              </div>
              <div className="p-3 bg-blue-500/10 rounded-full">
                <Users className="h-6 w-6 text-blue-500" strokeWidth={1.5} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-dashboard">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Oportunidades Activas</p>
                <p className="text-3xl font-light">{stats?.opportunity_stats?.open || 0}</p>
                <p className="text-xs text-muted-foreground mt-2">
                  {stats?.opportunity_stats?.total || 0} totales
                </p>
              </div>
              <div className="p-3 bg-emerald-500/10 rounded-full">
                <Building2 className="h-6 w-6 text-emerald-500" strokeWidth={1.5} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-dashboard">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Inmuebles en Pipeline</p>
                <p className="text-3xl font-light">{totalProperties}</p>
                <p className="text-xs text-muted-foreground mt-2">
                  {inReviewProperties} en revisión
                </p>
              </div>
              <div className="p-3 bg-amber-500/10 rounded-full">
                <TrendingUp className="h-6 w-6 text-amber-500" strokeWidth={1.5} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Capital by Status */}
      <Card className="card-dashboard">
        <CardHeader>
          <CardTitle className="text-lg">Capital por Estado</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {[
              { key: 'pending', label: 'Pendiente', color: 'amber' },
              { key: 'approved', label: 'Aprobado', color: 'blue' },
              { key: 'funds_pending', label: 'Fondos Pendientes', color: 'orange' },
              { key: 'confirmed', label: 'Confirmado', color: 'emerald' },
              { key: 'rejected', label: 'Rechazado', color: 'red' },
              { key: 'cancelled', label: 'Cancelado', color: 'gray' },
            ].map(({ key, label, color }) => {
              const data = capitalByStatus[key] || { count: 0, amount: 0 };
              return (
                <div key={key} className="p-4 bg-muted/30 rounded-sm">
                  <p className="text-xs text-muted-foreground mb-1">{label}</p>
                  <p className={`text-lg font-medium text-${color}-500`}>
                    {formatCurrency(data.amount || 0)}
                  </p>
                  <p className="text-xs text-muted-foreground">{data.count || 0} compromisos</p>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Investor Funnel */}
      <Card className="card-dashboard">
        <CardHeader>
          <CardTitle className="text-lg">Embudo de Inversionistas</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {[
              { 
                label: 'Usuarios Registrados', 
                count: totalUsers, 
                width: 100,
                color: 'bg-blue-500' 
              },
              { 
                label: 'KYC en Revisión', 
                count: pendingUsers, 
                width: totalUsers > 0 ? (pendingUsers / totalUsers) * 100 : 0,
                color: 'bg-amber-500' 
              },
              { 
                label: 'KYC Aprobados', 
                count: approvedUsers, 
                width: totalUsers > 0 ? (approvedUsers / totalUsers) * 100 : 0,
                color: 'bg-emerald-500' 
              },
              { 
                label: 'Con Compromiso de Inversión', 
                count: Object.values(capitalByStatus).reduce((sum, s) => sum + (s.count || 0), 0), 
                width: totalUsers > 0 ? (Object.values(capitalByStatus).reduce((sum, s) => sum + (s.count || 0), 0) / totalUsers) * 100 : 0,
                color: 'bg-primary' 
              },
              { 
                label: 'Inversión Confirmada', 
                count: capitalByStatus.confirmed?.count || 0, 
                width: totalUsers > 0 ? ((capitalByStatus.confirmed?.count || 0) / totalUsers) * 100 : 0,
                color: 'bg-emerald-600' 
              },
            ].map((step, index) => (
              <div key={index} className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm">{step.label}</span>
                  <span className="font-medium">{step.count}</span>
                </div>
                <div className="h-8 bg-muted rounded-sm overflow-hidden">
                  <div 
                    className={`h-full ${step.color} transition-all duration-500`}
                    style={{ width: `${Math.max(step.width, 2)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Status Breakdown Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Users by Status */}
        <Card className="card-dashboard">
          <CardHeader>
            <CardTitle className="text-lg">Usuarios por Estado de KYC</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {[
                { key: 'pending', label: 'Pendiente', icon: Clock, color: 'amber' },
                { key: 'in_review', label: 'En Revisión', icon: AlertCircle, color: 'blue' },
                { key: 'approved', label: 'Aprobado', icon: CheckCircle2, color: 'emerald' },
                { key: 'rejected', label: 'Rechazado', icon: AlertCircle, color: 'red' },
                { key: 'more_info_required', label: 'Info Requerida', icon: AlertCircle, color: 'orange' },
              ].map(({ key, label, icon: Icon, color }) => {
                const count = usersByStatus[key] || 0;
                const percentage = totalUsers > 0 ? (count / totalUsers) * 100 : 0;
                return (
                  <div key={key} className="flex items-center gap-4">
                    <div className={`p-2 rounded-full bg-${color}-500/10`}>
                      <Icon className={`h-4 w-4 text-${color}-500`} strokeWidth={1.5} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm">{label}</span>
                        <span className="text-sm font-medium">{count}</span>
                      </div>
                      <Progress value={percentage} className="h-2" />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Properties Pipeline */}
        <Card className="card-dashboard">
          <CardHeader>
            <CardTitle className="text-lg">Pipeline de Inmuebles</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {[
                { key: 'submitted', label: 'Enviados', color: 'blue' },
                { key: 'initial_review', label: 'Revisión Inicial', color: 'amber' },
                { key: 'legal_review', label: 'Revisión Legal', color: 'purple' },
                { key: 'technical_review', label: 'Revisión Técnica', color: 'indigo' },
                { key: 'financial_structuring', label: 'Estructuración', color: 'cyan' },
                { key: 'approved', label: 'Aprobados', color: 'emerald' },
                { key: 'rejected', label: 'Rechazados', color: 'red' },
              ].map(({ key, label, color }) => {
                const count = propertiesByStatus[key] || 0;
                const percentage = totalProperties > 0 ? (count / totalProperties) * 100 : 0;
                return (
                  <div key={key} className="flex items-center gap-4">
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm">{label}</span>
                        <span className="text-sm font-medium">{count}</span>
                      </div>
                      <div className="h-2 bg-muted rounded-sm overflow-hidden">
                        <div 
                          className={`h-full bg-${color}-500 transition-all duration-300`}
                          style={{ width: `${Math.max(percentage, 2)}%` }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Summary Metrics */}
      <Card className="card-dashboard border-primary/20 bg-primary/5">
        <CardContent className="pt-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center">
              <p className="text-sm text-muted-foreground mb-1">Tasa de Conversión KYC</p>
              <p className="text-2xl font-light text-primary">{conversionRate}%</p>
            </div>
            <div className="text-center">
              <p className="text-sm text-muted-foreground mb-1">Tasa de Inversión</p>
              <p className="text-2xl font-light text-primary">{investorRate}%</p>
              <p className="text-xs text-muted-foreground">de usuarios aprobados</p>
            </div>
            <div className="text-center">
              <p className="text-sm text-muted-foreground mb-1">Ticket Promedio</p>
              <p className="text-2xl font-light text-primary">
                {Object.values(capitalByStatus).reduce((sum, s) => sum + (s.count || 0), 0) > 0 
                  ? formatCurrency(totalCapitalCommitted / Object.values(capitalByStatus).reduce((sum, s) => sum + (s.count || 0), 0))
                  : '$0'}
              </p>
            </div>
            <div className="text-center">
              <p className="text-sm text-muted-foreground mb-1">Propiedades Aprobadas</p>
              <p className="text-2xl font-light text-primary">
                {totalProperties > 0 ? ((approvedProperties / totalProperties) * 100).toFixed(1) : 0}%
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminReportsPage;
